import { Injectable, EventEmitter } from '@angular/core';

import { TokenResponse } from '../../api/interfaces/token.response';

@Injectable()
export class TokenService {
  /**
   * This event fires, when the Expires In variable nearly expired.
   */
  public nearlyExpired: EventEmitter<string> = new EventEmitter<string>();

  /**
   * Token expiration timer id
   */
  private expirationTimer: any;

  /**
   * This variable stores the actual token.
   *
   * @private
   * @type {string}
   * @memberof TokenService
   */
  private cachedToken: string;

  /**
   * Name of the cookie, that stores the Access Token.
   */
  private readonly accessTokenCookieName = 'access_token';

  /**
   * Name of the cookie, that stores the Refresh Token.
   */
  private readonly refreshTokenCookieName = 'refresh_token';

  /**
   * Name of the cookie, that stores the Expires In variable.
   */
  private readonly expirationAtCookieName = 'token_expires_at';

  /**
   * Plus time added to the expires_in property.
   *
   * @private
   * @memberof OauthService
   */
  private readonly expiresInAddon = 1000;

  /**
   * Amount of time, when the token nearly expired (in ms).
   *
   * @private
   * @memberof OauthService
   */
  private readonly nearlyExpiredMs = 30000;

  constructor() {
    if (this.hasToken()) {
      this.startExpirationTimer();
    }
  }

  /**
   * This function returns the stored Access Token.
   */
  public getToken(): string {
    return this.cachedToken ? this.cachedToken : window.localStorage[this.accessTokenCookieName];
  }

  /**
   * This function stores the given token to the CookieStore.
   * @param token The token that came from the API.
   */
  public setToken(token: TokenResponse): void {
    const expireDate = new Date(Date.now() + this.expiresInAddon * +token.expires_in);
    window.localStorage[this.accessTokenCookieName] = token.access_token;
    window.localStorage[this.refreshTokenCookieName] = token.refresh_token;
    window.localStorage[this.expirationAtCookieName] = expireDate.getTime().toString();
    this.cachedToken = token.access_token;
    this.startExpirationTimer();
  }

  /**
   * This function returns `true` if there is any Access Token in the browser's CookieStore.
   */
  public hasToken(): boolean {
    return (
      !!window &&
      !!window.localStorage &&
      !!window.localStorage[this.accessTokenCookieName] &&
      !!window.localStorage[this.expirationAtCookieName] &&
      +window.localStorage[this.expirationAtCookieName] > Date.now()
    );
  }

  /**
   * This function removes the Access Token from the browser's CookieStore.
   */
  public deleteToken(): void {
    window.localStorage.removeItem(this.accessTokenCookieName);
    this.stopExpirationTimer();
  }

  /**
   * This function (re)starts the token expiration timer
   */
  private startExpirationTimer(): void {
    this.stopExpirationTimer();

    const expiresIn = +window.localStorage[this.expirationAtCookieName] - Date.now();
    if (expiresIn < this.nearlyExpiredMs) {
      this.nearlyExpired.emit(window.localStorage[this.refreshTokenCookieName]);
    } else {
      this.expirationTimer = setTimeout(() => {
        this.nearlyExpired.emit(window.localStorage[this.refreshTokenCookieName]);
      }, expiresIn - this.nearlyExpiredMs);
    }
  }

  /**
   * This function stops the token expiration timer
   */
  private stopExpirationTimer(): void {
    clearTimeout(this.expirationTimer);
  }
}
